import { Injectable } from "@angular/core";

export interface FeedbackAssessmentContext {
    formType: string | null;
    formTitle: string | null;
    licenseId: number | null;
    licenseName: string | null;
    sectionKey: string | null;
    requirementKey: string | null;
}

export interface FeedbackContextSnapshot {
    url: string;
    routeTitle: string | null;
    assessment: FeedbackAssessmentContext | null;
}

@Injectable({ providedIn: "root" })
export class FeedbackContextService {
    private routeUrl: string = "";
    private routeTitle: string | null = null;
    private assessmentContext: FeedbackAssessmentContext | null = null;

    updateRouteContext(url: string, title?: string | null): void {
        this.routeUrl = typeof url === "string" ? url : "";
        this.routeTitle = typeof title === "string" && title.trim() ? title.trim() : null;
    }

    updateAssessmentContext(context: FeedbackAssessmentContext | null): void {
        if (!context) {
            this.assessmentContext = null;
            return;
        }

        this.assessmentContext = {
            formType: this.normalizeString(context.formType),
            formTitle: this.normalizeString(context.formTitle),
            licenseId: Number.isInteger(context.licenseId as number) ? Number(context.licenseId) : null,
            licenseName: this.normalizeString(context.licenseName),
            sectionKey: this.normalizeString(context.sectionKey),
            requirementKey: this.normalizeString(context.requirementKey)
        };
    }

    getSnapshot(): FeedbackContextSnapshot {
        return {
            url: this.routeUrl,
            routeTitle: this.routeTitle,
            assessment: this.assessmentContext
        };
    }

    composeIssueDescription(message: string): string {
        const userMessage = this.normalizeMultiline(message);
        const snapshot = this.getSnapshot();

        const lines = [userMessage];

        lines.push("", "---", "Feedback context:");
        lines.push(`- Page URL: ${snapshot.url || "Unknown"}`);
        lines.push(`- Page title: ${snapshot.routeTitle || "Not specified"}`);

        if (snapshot.assessment) {
            const assessment = snapshot.assessment;
            lines.push(`- Assessment type: ${assessment.formType || "Unknown"}`);

            if (assessment.formTitle) {
                lines.push(`- Assessment title: ${assessment.formTitle}`);
            }

            if (assessment.licenseId) {
                lines.push(`- Product license ID: ${assessment.licenseId}`);
            }

            if (assessment.licenseName) {
                lines.push(`- Product license: ${assessment.licenseName}`);
            }

            if (assessment.sectionKey) {
                lines.push(`- Section: ${assessment.sectionKey}`);
            }

            if (assessment.requirementKey) {
                lines.push(`- Requirement: ${assessment.requirementKey}`);
            }
        }

        return lines.join("\n");
    }

    private normalizeString(value: unknown): string | null {
        if (typeof value !== "string") {
            return null;
        }

        const normalized = value.trim();
        return normalized || null;
    }

    private normalizeMultiline(value: unknown): string {
        if (typeof value !== "string") {
            return "";
        }

        return value.replace(/\r\n/g, "\n").trim();
    }
}
